import { Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import type { ContentChipLabel, LifecycleActionsView, PublishedStateView, Translate } from './content-types'
import { StatusChipView } from './status-chip.view'

/** The lifecycle actions a button can start. */
export type LifecycleAction =
	| 'save'
	| 'submit'
	| 'approve'
	| 'reject'
	| 'pullBack'
	| 'publish'
	| 'retire'
	| 'newVersion'

/**
 * Props for {@link LifecycleBarView}.
 *
 * @property label - The localization's status chip label.
 * @property published - The in-force / scheduled line of a published localization.
 * @property canSave - A draft the viewer may edit.
 * @property actions - Which review/publish actions render (the page hook applies permissions and eligibility).
 * @property pending - True while the approval track needed for eligibility is loading.
 */
export interface LifecycleBarViewProps {
	t: Translate
	label: ContentChipLabel
	published: PublishedStateView | null
	canSave: boolean
	actions: LifecycleActionsView
	pending: boolean
	isBusy: boolean
	error: string | null
	onAction: (action: LifecycleAction) => void
}

const stateLine = (t: Translate, published: PublishedStateView | null): string | null => {
	if (!published?.from) return null
	if (published.scheduled) return t('lifecycle.scheduledFrom', { date: published.from })
	return t('lifecycle.inForceSince', { from: published.from, to: published.to ?? t('dates.openEnded') })
}

/** Status, in-force line and the status-specific actions of one localization (organism). */
export function LifecycleBarView(props: LifecycleBarViewProps): ReactElement {
	const { t, actions } = props
	const button = (action: LifecycleAction, variant: 'primary' | 'muted' | 'danger' = 'primary', disabled = false) => (
		<Button key={action} variant={variant} isDisabled={props.isBusy || disabled} onPress={() => props.onAction(action)}>
			{t(`lifecycle.${action}`)}
		</Button>
	)
	const line = stateLine(t, props.published)
	return (
		<div className="flex flex-col gap-2">
			<div className="flex flex-wrap items-center gap-2">
				<StatusChipView t={t} label={props.label} />
				{line ? <span className="text-ds-sm text-ds-text">{line}</span> : null}
			</div>
			{props.error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{props.error}
				</p>
			) : null}
			{props.pending ? (
				<p className="text-ds-sm text-ds-text-muted">{t('lifecycle.loading')}</p>
			) : (
				<div className="flex flex-wrap gap-2">
					{props.canSave ? button('save') : null}
					{actions.submit ? button('submit', 'primary', actions.submitDisabled) : null}
					{actions.approve ? button('approve') : null}
					{actions.reject ? button('reject', 'danger') : null}
					{actions.pullBack ? button('pullBack', 'muted') : null}
					{actions.publish ? button('publish') : null}
					{actions.retire ? button('retire', 'danger') : null}
					{actions.newVersion ? button('newVersion', 'muted') : null}
				</div>
			)}
			{actions.submit && actions.submitDisabled ? (
				<p className="text-ds-sm text-ds-text-muted">{t('lifecycle.saveFirst')}</p>
			) : null}
		</div>
	)
}
